/**
 * The status language, in one place.
 *
 * Six device states plus a neutral, and every screen uses this rather than
 * composing `pill ok` / `tag fail` / `sev-critical` by hand — three vocabularies
 * for one idea, which is how the same fault came to read green in the grid and
 * red on the card at the same moment.
 *
 * `reason` is the sentence behind the colour. It goes in a tooltip rather than
 * beside the pill so a table can carry forty of these without forty sentences;
 * the colour is never the whole message, but it is allowed to be the visible
 * part of it.
 */

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type React from 'react';

import type { CellState } from '../fleet-state.ts';

export type PillState = CellState | 'neutral';

/** Alert severity onto the same bands, so a severity the database grows later lands on neutral rather than unstyled. */
export function severityState(severity: string): PillState {
  switch (severity) {
    case 'critical':
      return 'fault';
    case 'warning':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Check outcome onto the same bands. */
export function outcomeState(outcome: string): PillState {
  switch (outcome) {
    case 'pass':
      return 'ok';
    case 'fail':
      return 'fault';
    case 'unavailable':
      return 'unmonitored';
    default:
      return 'stale';
  }
}

export function Pill({
  state = 'neutral',
  reason,
  count = false,
  children,
}: {
  state?: PillState;
  reason?: string | null;
  /** A quantity, not a status: keeps the neutral surface with tabular digits. */
  count?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const classes = ['pill'];
  if (state !== 'neutral') classes.push(state);
  if (count) classes.push('count');
  const pill = <span className={classes.join(' ')}>{children}</span>;
  if (reason === undefined || reason === null || reason === '') return pill;
  return (
    <TooltipPrimitive.Root delayDuration={200}>
      <TooltipPrimitive.Trigger asChild>{pill}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content className="tooltip" sideOffset={6}>
          {reason}
          <TooltipPrimitive.Arrow className="tooltip-arrow" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/** One provider at the root; Radix tooltips need it and it costs nothing. */
export const TooltipProvider = TooltipPrimitive.Provider;

/** A plain tooltip on anything. */
export function Tooltip({
  content,
  children,
}: {
  content: React.ReactNode;
  children: React.ReactElement;
}): React.JSX.Element {
  return (
    <TooltipPrimitive.Root delayDuration={200}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content className="tooltip" sideOffset={6}>
          {content}
          <TooltipPrimitive.Arrow className="tooltip-arrow" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
